import { createContext, useContext } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { UserDto } from '@br/protocol';
import { useMe } from '../api/queries';
import { errorMessage } from '../api/client';
import { Alert, Button, FullPageSpinner } from '../components/ui';
import { AppSocketProvider } from '../realtime/AppSocket';

export interface AuthValue {
  user: UserDto;
  org: { id: string; name: string };
  isAdmin: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth outside RequireAuth');
  return v;
}

/** Route wrapper: renders children only for a logged-in operator, otherwise sends to /login. */
export function RequireAuth() {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <FullPageSpinner label="로그인 상태 확인 중" />;
  if (me.isError) {
    return (
      <div className="mx-auto mt-24 max-w-md space-y-3 px-4">
        <Alert level="error">서버에 연결할 수 없습니다: {errorMessage(me.error)}</Alert>
        <Button onClick={() => me.refetch()}>다시 시도</Button>
      </div>
    );
  }
  if (!me.data) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  const value: AuthValue = {
    user: me.data.user,
    org: me.data.org,
    isAdmin: me.data.user.role === 'admin',
  };
  return (
    <AuthContext.Provider value={value}>
      <AppSocketProvider>
        <Outlet />
      </AppSocketProvider>
    </AuthContext.Provider>
  );
}
